import React, { Fragment } from 'react';
import InfiniteScrollTable from "../../infinite-scroll-table";
import {Icon, Radio, Modal, Select} from "antd";
import {translate} from "react-i18next";
import {MARGIN_MD, MARGIN_SM} from "../../../styles/constants";
import DataLanguageSelector from "../../data-language-selector";
import { compose } from 'redux';
import { EL_COL_TYPE_ATTRIBUTE, EL_COL_TYPE_DIMENSION, EL_COL_TYPE_IGNORE, 
  EL_COL_TYPE_MEASURE, EL_COL_TYPE_TRANSLATION } from '../constants';
import { columnCanBeTranslated } from './utils';

const TABLE_COL_WIDTH = 110;
const TABLE_COL_TRANSLATION_WIDTH = 280;
const TABLE_SELECT_WIDTH = 120;

const CenteredRadio = ({checked, onChange}) =>
  <div style={{width: "100%", textAlign: "center"}}>
    <Radio checked={checked} onChange={onChange}/>
  </div>;


const ELStructureStepTable = ({
                                t,
                                structureColumns,
                                permissions,
                                onChange,
                              }) => {

  const canCreateCodelists = permissions && permissions.rules && permissions.rules.includes("CanUpdateStructuralMetadata");

  const columns = [      
    {
      dataIndex: "id"
    },
    {
      dataIndex: "isDimension",
      title:
        <span>
          {t('components.easyLoading.structure.table.dimension.title')}
          <Icon type="bar-chart" style={{marginLeft: MARGIN_SM}}/>
        </span>,
      width: TABLE_COL_WIDTH,
      noFilter: true,
      noSort: true,
      render: (isDimension,row) => 
        <CenteredRadio 
          checked={isDimension}
          onChange={() => onChange(row.id, EL_COL_TYPE_DIMENSION, null, null)}
        />,
      renderText: isDimension => isDimension
        ? t('components.easyLoading.structure.table.dimension.values.true')
        : t('components.easyLoading.structure.table.dimension.values.false')
    },
    {
      dataIndex: "isMeasure",
      title:
        <span>
          {t('components.easyLoading.structure.table.measure.title')}
          <Icon type="line-chart" style={{marginLeft: MARGIN_SM}}/>
        </span>,
      width: TABLE_COL_WIDTH,
      noFilter: true,
      noSort: true,
      render: (isMeasure, row) => 
        <CenteredRadio 
          checked={isMeasure}
          onChange={() => {
            if (structureColumns.some(c => c.type === EL_COL_TYPE_MEASURE)) {
              Modal.error({
                title: t('components.easyLoading.structure.table.alerts.onlyOneMeasureAllowed.title'),
                content: t('components.easyLoading.structure.table.alerts.onlyOneMeasureAllowed.content')
              });
            } else {
              onChange(row.id, EL_COL_TYPE_MEASURE, null, null)
            }
            
          }}
        />,
      renderText: isMeasure => isMeasure
        ? t('components.easyLoading.structure.table.measure.values.true')
        : t('components.easyLoading.structure.table.measure.values.false')
    },
    {
      dataIndex: "isAttribute",
      title:
        <span>
          {t('components.easyLoading.structure.table.attribute.title')}
          <Icon type="profile" style={{marginLeft: MARGIN_SM}}/>
        </span>,
      width: TABLE_COL_WIDTH,
      noFilter: true,
      noSort: true,
      render: (isAttribute, row) => 
        <CenteredRadio 
          checked={isAttribute}
          onChange={() => onChange(row.id, EL_COL_TYPE_ATTRIBUTE, null, null)}
        />,
      renderText: isAttribute => isAttribute
        ? t('components.easyLoading.structure.table.attribute.values.true')
        : t('components.easyLoading.structure.table.attribute.values.false')
    }     
  ].concat(canCreateCodelists 
    ? {
        dataIndex: "translation",
        title:
          <span>
            {t('components.easyLoading.structure.table.translation.title')}
            <Icon type="global" style={{marginLeft: MARGIN_SM}}/>
          </span>,
        width: TABLE_COL_TRANSLATION_WIDTH,
        noFilter: true,
        noSort: true,
        render: (translation, row) =>
          <div>
            <Radio 
              checked={translation !== null}
              onChange={() => onChange(row.id, EL_COL_TYPE_TRANSLATION, null, null)}
              style={{marginRight: MARGIN_MD}}
            />
            {translation && (
              <Fragment>
                <Select style={{marginRight: MARGIN_MD, width: TABLE_SELECT_WIDTH}}
                  onChange={(value) => onChange(row.id, EL_COL_TYPE_TRANSLATION, value, null)}
                  value={row.translationDefinition.transColId}
                >                      
                  {(structureColumns || [])
                  .filter(column => columnCanBeTranslated(column))
                  .map((column, index) => {
                    return (
                      <Select.Option
                        key={index}
                        value={column.id}
                      >
                        {column.id}
                      </Select.Option>
                    )
                  })}                    
                </Select>
                <DataLanguageSelector 
                  value={row.translationDefinition.langCode} 
                  showLangCode 
                  style={{width: 90}}
                  onSelect={
                    lang => onChange(row.id, EL_COL_TYPE_TRANSLATION, row.translationDefinition.transColId, lang)
                  }/>
              </Fragment>
            )}
                              
          </div>,              
        renderText: translation => {
          let text = null;
          if (translation) {            
            if (translation.transColId) {
              text = `${translation.transColId}`
              if (translation.langCode) {
                text = `${text} (${translation.langCode.toUpperCase()})`
              }
            }
          }
          return text;
        }     
      }
    : [])
    .concat(
      {
        dataIndex: "isIgnored",
        title:
          <span>
            {t('components.easyLoading.structure.table.ignore.title')}
            <Icon type="delete" style={{marginLeft: MARGIN_SM}}/>
          </span>,
        width: TABLE_COL_WIDTH,
        noFilter: true,
        noSort: true,
        render: (isIgnored, row) => 
          <CenteredRadio 
            checked={isIgnored}
            onChange={() => onChange(row.id, EL_COL_TYPE_IGNORE, null, null)}
          />,
        renderText: isIgnored => isIgnored
          ? t('components.easyLoading.structure.table.ignore.values.true')
          : t('components.easyLoading.structure.table.ignore.values.false')
      }
    );
  
  return (             
    <InfiniteScrollTable
      data={(structureColumns || []).map(column => ({
        ...column,
        isDimension: column.type === EL_COL_TYPE_DIMENSION,
        isMeasure: column.type === EL_COL_TYPE_MEASURE,
        isAttribute: column.type === EL_COL_TYPE_ATTRIBUTE,
        translation: column.type === EL_COL_TYPE_TRANSLATION
          ? {
              langCode: column.translationDefinition.langCode,
              transColId: column.translationDefinition.transColId
            }
          : null,
        isIgnored: column.type === EL_COL_TYPE_IGNORE
      }))}
      getRowKey={row => row.id}
      columns={columns}
    />
  )
};

export default compose(
  translate()
)(ELStructureStepTable);


// WEBPACK FOOTER //
// ./src/components/easy-loading/structure-step/Table.jsx